import { getCurrentUser } from '@creditsights/api-ui-web';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useState } from 'react';
import { ContentTypeFilter } from '../../models';
import { FEED_WIDGETS_KEY, getFeedWidgets, WIDGET_ID } from '../../pages/Feeds/Feed.constants';
import useOnDrag, { useOnDragResult } from '../common/useOnDrag';
import useFetchUserPermissions from '../permissions/useFetchUserPermissions';

interface FeedDraggableState {
  rightSideWidgets: WIDGET_ID[];
  isContainerActive: boolean;
}

const useOnDragFeed = (articleTypeFilter: ContentTypeFilter): useOnDragResult<FeedDraggableState> => {
  const { data: userPermissions } = useFetchUserPermissions();
  const { userId } = getCurrentUser();
  const feedWidgetsKey = `${FEED_WIDGETS_KEY}_${articleTypeFilter}_${userId}`;
  const defaultFeedWidgets = getFeedWidgets(userPermissions!.userSubscription, articleTypeFilter);

  const [isContainerActive, setIsContainerActive] = useState<boolean>(false);

  const handleDragStart = () => {
    setIsContainerActive(true);
  };

  const handleDragEnd = (event: DragEndEvent, rightSideWidgets, setDraggableState) => {
    const { over, active } = event;
    const overId = over?.id;

    if (!isContainerActive || !overId) {
      setIsContainerActive(false);
      return;
    }

    const activeWidgetIndex = rightSideWidgets.indexOf(active.id);
    const overWidgetIndex = rightSideWidgets.indexOf(overId);

    if (activeWidgetIndex !== overWidgetIndex) {
      setDraggableState(arrayMove(rightSideWidgets, activeWidgetIndex, overWidgetIndex));
    }
    setIsContainerActive(false);
  };

  const {
    onDragStart,
    onDragEnd,
    onDragOver,
    draggableState: rightSideWidgets,
    resetDraggableState,
  } = useOnDrag(feedWidgetsKey, defaultFeedWidgets, {
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
  });
  const userSortedWidgets = [...defaultFeedWidgets].sort(
    (a, b) => rightSideWidgets.indexOf(a) - rightSideWidgets.indexOf(b)
  );

  return {
    onDragStart,
    onDragEnd,
    onDragOver,
    draggableState: { rightSideWidgets: userSortedWidgets, isContainerActive },
    resetDraggableState,
  };
};

export default useOnDragFeed;
